import { Dialog, Tooltip, DialogContent, DialogTitle} from "@mui/material";
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { Row, Col } from 'reactstrap';
import { FcViewDetails } from "react-icons/fc";

import { StatusService } from '../../services/StatusService';

import Routes from "../../helpers/Routes";
import Toast from "../../helpers/Toast";

import ButtonComponent from '../../components/ButtonComponent';
import TableComponent from "../../components/TableComponent";
import { GetPossibleStatusByBaseStatus } from "../../models/Status/GetPossibleStatusByBaseStatusDTO";
import { NextPossibleStatusDTO } from "../../models/Status/NextPossibleStatusDTO";
import NextStatusPermissions from "./NextStatusPermissions";
import CreateNextPossibleStatus from "./CreateNextPossibleStatus";


type NextPossibleStatusProps = {
    BaseStatus: string;
    baseStatusName: string;
}

function NextPossibleStatusFromStatus(props: NextPossibleStatusProps){
    const statusService = new StatusService();
    const intl = useIntl();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const[nextPossibleStatus, setNextPossibleStatus] = useState<NextPossibleStatusDTO[]>([]);
    const [nextStatusId, setNextStatusId] = useState<string>("");
    const [nextStatusName, setNextStatusName] = useState<string>("");
    const [IsNextStatusPermissionsModalOpen, setIsNextStatusPermissionsModalOpen] = useState(false);
    const [IsCreateRelationModalOpen, setIsCreateRelationModalOpen] = useState(false);
    const pageSizes: number[] = [5, 15, 30];
    const [pageSize, setPageSize] = useState<number>(pageSizes[0]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [goToPage, setGoToPage] = useState<number>();
    const [pageCount, setPageCount] = useState(1);
    const [deleteParams, setDeleteParams] = useState<{ firstId: number; secondId: number } | null>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const columns = React.useMemo(
            () => [
              {     
                Header: <FormattedMessage id="Screens.User.Info.BaseStatus" />,
                accessor: "baseStatusName",
                width: 50,
              },
              {     
                Header: <FormattedMessage id="Screens.User.Info.NextStatus" />,
                accessor: "nextStatusName",
                width: 50,
              },
              {
                Header: "Ver próximo estado",
                accessor: "",
                Cell: (props: any) => {
                  const rowId = props.cell.row.original.nextStatusId;
                  const rowName = props.cell.row.original.nextStatusName;
                  return (
                    <div style={{ display: "flex" }}>
                      {
                      <ButtonComponent
                        text={intl.formatMessage({ id: "Screens.Status.Button.Select" })}
                        onClick={() => navigate(`${Routes.status.details}/${rowId}`, {state : { baseStatusName: rowName } })}
                          xs={12}
                          sm={12}
                          md={12}
                          lg={12}
                          xl={12}
                      />
                      }
                    </div>
                  );
                },
                width: 1,
              },
              {
                Header: "",
                accessor: " ",
                Cell: (props: any) => {
                  const rowId = props.cell.row.original.nextStatusId;
                  const rowName = props.cell.row.original.nextStatusName;
                  return (
                    <div style={{ display: "flex" }}>
                      {
                        <Tooltip
                          title="Perfis que podem editar neste estado"
                          aria-label="Perfis que podem editar neste estado"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              alignContent: "center",
                              flexDirection: "column",
                              cursor: "pointer",
                              height: 30,
                            }}
                          >
                            <FcViewDetails
                            className="icons" 
                            onClick={() => {
                              setNextStatusId(rowId);
                              setNextStatusName(rowName);
                              setIsNextStatusPermissionsModalOpen(true);
                            }}
                            />
                          </div>
                        </Tooltip>
                      }
                    </div>
                  );
                },
                width: 1,
              },
              {
                  Header: "", 
                  accessor: "actions",
                  Cell: ({ row }: any) => (
                      <ButtonComponent
                      text={intl.formatMessage({ id: "General.Delete" })}
                      onClick={() => handleDialogDelete(row.original.baseStatusId, row.original.nextStatusId)}
                      />
                  ),
              },
            ],
          []
        );

                          
        const GetNextPossibleStatus = async ( currentPage: number, pageSize: number ) => {

            setIsLoading(true);
            const data: GetPossibleStatusByBaseStatus = {
                id : String(props.BaseStatus), 
                currentPage : currentPage,
                pageSize : pageSize,
            }

            var response = await statusService.GetPossibleStatusByBaseStatus(data);

            if(response.success === false){
              Toast.Show("error", response.message);
              setIsLoading(false);
            return;
            }

            if(response.items.length == 0){
              Toast.Show("info", "Ainda não existem estados relacionados com este.");
              setIsLoading(false);
              return;
            }
            
            setNextPossibleStatus(response.items);
            setPageCount(response.totalPages);
            setPageSize(pageSize);
            setCurrentPage(currentPage);
            setGoToPage(0);
            setIsLoading(false);
        }

        useEffect(() => {
            if(props.BaseStatus !== ""){
                GetNextPossibleStatus(currentPage ?? 1, pageSize ?? 5)
            }
        }, [props.BaseStatus, currentPage, pageSize]);

        const handleCloseNextStatusPermissionsModal = () => {
          setIsNextStatusPermissionsModalOpen(false);
          GetNextPossibleStatus(currentPage ?? 1, pageSize ?? 5);
        }

        const onNewTaskClick = () => {
          setIsCreateRelationModalOpen(true);
        };

        const handleCloseCreateRelationModal = () => {
          setIsCreateRelationModalOpen(false);
          GetNextPossibleStatus(currentPage ?? 1, pageSize ?? 5);
        }

        const handleDialogDelete = (firstId: number, secondId: number) => {
          setDeleteParams({ firstId, secondId });
          setConfirmDelete(true);
        };

        const handleConfirmDelete = async () => {
            if (deleteParams) {
                const result = await statusService.DeleteNextPossibleStatus(deleteParams.firstId, deleteParams.secondId);
                if (result.success) {
                    Toast.Show("success", result.message ?? "Estado apagado com sucesso.");
                    GetNextPossibleStatus(currentPage, pageSize);
                } else {
                    Toast.Show("error", result.message ?? "Erro ao apagar estado.");
                }
        };
            setConfirmDelete(false);
            setDeleteParams(null);
        }

        const handleCancelDelete = () => {
          setConfirmDelete(false);
          setDeleteParams(null);
        };

    return (
          <Row>
          <Col xl={12}>
            <Row>
                <ButtonComponent
                    text={intl.formatMessage({ id: "Screens.Status.Button.CreateRelation" })}
                    onClick={() => onNewTaskClick()}
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    style={{ minHeight: "37px", minWidth: "100px", marginTop: "25px", marginBottom: "25px" }}
                />
            </Row>
            <div>
              <label style={{ margin: "0px", padding: "13px" }}><b><FormattedMessage id="Screens.Status.Navbar.NextPossibleStatus" /></b></label>
                
                <TableComponent
                    columns={columns}
                    data={nextPossibleStatus}
                    goToPage={goToPage}
                    initialPage={currentPage}
                    pageCount={pageCount}
                    initialPageSize={pageSize}
                    pageSizes={pageSizes}
                    manualPagination={true}
                    loading={isLoading}
                    fetchData={GetNextPossibleStatus}
                    initialSortBy={[]}
                    disableSortBy={true}
                />
                </div>
                <Dialog open={IsCreateRelationModalOpen} onClose={() => setIsCreateRelationModalOpen(false)} fullWidth maxWidth="sm"> 
                  <DialogTitle>Criar Relação</DialogTitle>
                    <DialogContent>
                      <CreateNextPossibleStatus
                        BaseStatus={props.BaseStatus}
                        baseStatusName={props.baseStatusName}
                        onCancel={() => setIsCreateRelationModalOpen(false)}
                        onSuccess={() => handleCloseCreateRelationModal()}
                      />
                    </DialogContent>
                </Dialog>
                <Dialog open={IsNextStatusPermissionsModalOpen} onClose={() => setIsNextStatusPermissionsModalOpen(false)} fullWidth maxWidth="sm"> 
                  <DialogTitle>Ver permissões do próximo estado</DialogTitle>
                    <DialogContent>
                      <NextStatusPermissions
                        BaseStatus={props.BaseStatus}
                        NextStatus={nextStatusId}
                        nextStatusName={nextStatusName}
                        onCancel={() => setIsNextStatusPermissionsModalOpen(false)}
                        onSuccess={() => handleCloseNextStatusPermissionsModal()}
                      />
                    </DialogContent>
                </Dialog>
                <Dialog
                    open={confirmDelete}
                    onClose={handleCancelDelete}
                    fullWidth
                    >
                    <DialogTitle>
                        {intl.formatMessage({ id: "Screens.Status.NextPossibleStatus.ConfirmDelete.Title"})}
                    </DialogTitle>
                    <DialogContent>
                        <p>{intl.formatMessage({ id: "Screens.Status.NextPossibleStatus.Confirm.Reject"})}</p>
                    </DialogContent>
                    <div style={{ display: "flex", justifyContent: "flex-end", padding: "16px" }}>
                        <ButtonComponent
                            text={intl.formatMessage({ id: "Common.Buttons.Cancel"})}
                            onClick={handleCancelDelete}
                            style={{ marginRight: "8px" }}
                        />
                        <ButtonComponent
                            text={intl.formatMessage({ id: "General.Delete"})}
                            onClick={handleConfirmDelete}
                        />
                    </div>
                </Dialog>
              </Col>
          </Row>
   );
}

export default NextPossibleStatusFromStatus